import React from 'react';
import { connect } from 'react-redux';

import { operations as appOps } from '../redux/App';
import { User } from '../types/client';
import { authenticateUser } from '../utils';

interface UserContextProps {
    children: React.ReactNode;
    user: User;
    fetchUser: () => Promise<User>;
}

interface UserContextValue extends User {
    fetchedUser: boolean;
}

const UserContext = React.createContext<UserContextValue>({} as UserContextValue);

const Provider = ({ children, user, fetchUser }: UserContextProps) => {
    const [fetchedUser, setFetchedUser] = React.useState(false);

    const getUserFromToken = React.useCallback(async () => {
        if (!fetchedUser) {
            // This block authenticates a user that doesn't log in directly on our login page
            // usually a redirect from a client landing page/app etc.
            if (window.location.search) {
                const url = new URL(window.location.href);
                const token = url.searchParams.get('__csst');
                let searchVal = url.search;
                if (token) searchVal = '';
                window.history.replaceState(
                    null,
                    '',
                    `${url.origin}${url.pathname}${searchVal}${url.hash}`,
                );
                if (token) authenticateUser(token);
            }
            await fetchUser();
            setFetchedUser(true);
        }
    }, [fetchedUser, fetchUser]);

    React.useEffect(() => {
        getUserFromToken();
    }, [getUserFromToken]);

    return <UserContext.Provider value={{ ...user, fetchedUser }}>{children}</UserContext.Provider>;
};

const mapStateToProps = (state: { app: { user: User } }) => ({
    user: state.app.user,
});

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
const mapDispatchToProps = (dispatch: any) => ({
    fetchUser: () => dispatch(appOps.fetchUser()),
});

const UserProvider = connect(mapStateToProps, mapDispatchToProps)(Provider);

export { UserProvider };

export default UserContext;
